'use client';

import { ChevronRight } from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Container,
  GridLegacy as Grid,
  Link,
  Typography,
} from '@mui/material';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import LoadingSpinner from '../../components/atoms/LoadingSpinner';
import OrangeLine from '../../components/atoms/OrangeLine';
import CategoryTabs from '../../components/molecules/CategoryTabs';
import NewsCard from '../../components/molecules/NewsCard';
import NewsPagination from '../../components/molecules/NewsPagination';
import { useNews, useNewsCategories } from '../../hooks/useNews';
import { NewsArticle } from '../../services/types/news';

export default function NewsContent() {
  const router = useRouter();

  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const pageSize = 9;

  // Initialize state from URL parameters using router
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const page = searchParams.get('page');
      const category = searchParams.get('category');

      if (page) {
        setCurrentPage(parseInt(page, 10));
      }
      if (category) {
        setActiveCategory(category);
      }
    }
  }, []);

  // Fetch data
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useNewsCategories();
  const {
    data: newsData,
    loading: newsLoading,
    error: newsError,
    refetch,
  } = useNews({
    page: currentPage,
    pageSize,
    category: activeCategory || undefined,
  });

  const handleCategoryChange = (category: string | null) => {
    setActiveCategory(category);
    setCurrentPage(1);

    // Update URL parameters
    const params = new URLSearchParams();
    if (category) {
      params.set('category', category);
    }
    params.set('page', '1');

    const queryString = params.toString();
    const newUrl = `/news${queryString ? `?${queryString}` : ''}`;
    router.push(newUrl);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    // Update URL parameters
    const params = new URLSearchParams();
    if (activeCategory) {
      params.set('category', activeCategory);
    }
    params.set('page', page.toString());

    const queryString = params.toString();
    const newUrl = `/news${queryString ? `?${queryString}` : ''}`;
    router.push(newUrl);

    // Scroll to top - use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  // Show loading state for categories
  if (categoriesLoading) {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth='lg'>
          <LoadingSpinner message='Đang tải danh mục...' />
        </Container>
      </Box>
    );
  }

  // Show error state for categories
  if (categoriesError) {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth='lg'>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography
              variant='h6'
              sx={{
                color: '#ff4444',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                mb: 2,
              }}
            >
              Lỗi tải danh mục
            </Typography>
            <Typography
              sx={{
                color: '#666666',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                mb: 3,
              }}
            >
              {categoriesError}
            </Typography>
            <button
              onClick={() => router.refresh()}
              style={{
                padding: '12px 24px',
                backgroundColor: '#EB722E',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              Thử lại
            </button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ backgroundColor: '#F7F9FC' }}>
        <Container maxWidth='lg'>
          {/* Breadcrumb and Title Section */}
          <Box sx={{ py: { xs: 6, md: 10 } }}>
            {/* Breadcrumb */}
            <Box sx={{ mb: 6 }}>
              <Breadcrumbs
                separator={
                  <ChevronRight sx={{ color: '#999999', fontSize: 16 }} />
                }
                sx={{ mb: 0 }}
              >
                <Link
                  href='/'
                  underline='none'
                  sx={{
                    color: '#999999',
                    fontSize: '16px',
                    fontFamily:
                      'Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
                    '&:hover': {
                      color: '#666666',
                    },
                  }}
                >
                  Trang chủ
                </Link>
                <Typography
                  sx={{
                    color: '#1A1A1A',
                    fontSize: '16px',
                    fontWeight: 500,
                    fontFamily:
                      'Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
                  }}
                >
                  Tin tức
                </Typography>
              </Breadcrumbs>
            </Box>

            {/* Title Section */}
            <Box>
              <Typography
                variant='h1'
                component='h1'
                sx={{
                  fontFamily:
                    'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '32px', md: '40px' },
                  lineHeight: 1.45,
                  color: '#001A44',
                  mb: 2,
                }}
              >
                TIN TỨC
              </Typography>
              <OrangeLine width='190px' height='5px' />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Category Tabs */}
      <Box sx={{ backgroundColor: '#EB722E' }}>
        <Container maxWidth='lg'>
          <CategoryTabs
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />
        </Container>
      </Box>

      {/* News Grid */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth='lg'>
          {newsLoading ? (
            <LoadingSpinner message='Đang tải tin tức...' />
          ) : newsError ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography
                variant='h6'
                sx={{
                  color: '#ff4444',
                  fontFamily:
                    'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  mb: 2,
                }}
              >
                Lỗi tải tin tức
              </Typography>
              <Typography
                sx={{
                  color: '#666666',
                  fontFamily:
                    'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  mb: 3,
                }}
              >
                {newsError}
              </Typography>
              <button
                onClick={() => refetch()}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#EB722E',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontFamily:
                    'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >
                Thử lại
              </button>
            </Box>
          ) : newsData.data.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography
                variant='h6'
                sx={{
                  color: '#666666',
                  fontFamily:
                    'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  mb: 2,
                }}
              >
                Không có tin tức nào
              </Typography>
              <Typography
                sx={{
                  color: '#999999',
                  fontFamily:
                    'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >
                {activeCategory
                  ? `Không có tin tức nào trong danh mục "${categories.find(c => c.slug === activeCategory)?.name || activeCategory}"`
                  : 'Hiện tại chưa có tin tức nào được đăng tải.'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* News Grid */}
              <Grid container spacing={3}>
                {newsData.data.map((article: NewsArticle) => (
                  <Grid item xs={12} md={6} lg={4} key={article.id}>
                    <NewsCard article={article} />
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {newsData.meta?.pagination?.pageCount > 1 && (
                <NewsPagination
                  currentPage={currentPage}
                  totalPages={newsData.meta.pagination.pageCount}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </Container>
      </Box>
    </Box>
  );
}
